import { useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, Clock, MapPin, CheckCircle2, AlertCircle, MessageSquare } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { BUSINESS, SIZE_PRICING, DIFFICULTY, CLEANUP_UPSELLS, IMAGES } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
    "w-full bg-brand-elevated border border-brand-line rounded px-4 py-3 text-brand-cream text-base placeholder:text-brand-moss focus:outline-none focus:border-brand-amber transition-colors";

const Quote = () => {
    const location = useLocation();
    const prefill = location.state || {};
    const formRef = useRef(null);

    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        address: "",
        stump_size: prefill.size || "",
        situation: prefill.situation || "",
        cleanup: prefill.cleanups?.join(", ") || "",
        message: "",
    });
    const [status, setStatus] = useState("idle");
    const [result, setResult] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMsg("");
        try {
            const payload = {
                name: form.name,
                phone: form.phone,
                email: form.email || null,
                address: form.address || null,
                stump_size: form.stump_size || null,
                situation: form.situation || null,
                cleanup: form.cleanup || null,
                message: form.message || null,
            };
            const res = await axios.post(`${API}/quotes`, payload);
            setResult(res.data);
            setStatus("success");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setErrorMsg(
                typeof detail === "string"
                    ? detail
                    : Array.isArray(detail)
                    ? detail[0]?.msg || "Please check your details and try again."
                    : "Something went wrong. Please call or text us instead."
            );
            setStatus("error");
        }
    };

    const reset = () => {
        setForm({
            name: "",
            phone: "",
            email: "",
            address: "",
            stump_size: "",
            situation: "",
            cleanup: "",
            message: "",
        });
        setResult(null);
        setStatus("idle");
    };

    return (
        <main className="bg-brand-ink">
            <Seo
                title="Free Stump Grinding Quote"
                description="Get a free stump grinding quote in Poplar Bluff, MO. Tell us the stump size and location, or text a photo to (573) 300-8625. Same-week scheduling guaranteed."
            />
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-40 pb-24">
                <Reveal className="mb-12">
                    <Eyebrow className="mb-4">Free quote</Eyebrow>
                    <h1 className="font-display font-black uppercase text-brand-cream text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.92] mb-6">
                        Tell us about <span className="text-brand-amber">your stump.</span>
                    </h1>
                    <p className="text-brand-sage text-base sm:text-lg leading-relaxed max-w-2xl">
                        Send the form and we'll call you back during business
                        hours — or skip the line and call or text{" "}
                        <a
                            href={BUSINESS.phoneTel}
                            data-testid="quote-inline-phone-link"
                            className="text-brand-amber font-semibold hover:underline"
                        >
                            {BUSINESS.phoneDisplay}
                        </a>
                        .
                    </p>
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                    <div className="lg:col-span-3">
                        {status === "success" && result ? (
                            <div
                                data-testid="quote-success-panel"
                                className="rounded-lg border border-[#75E29A]/40 bg-brand-forest/40 p-8 sm:p-10"
                            >
                                <CheckCircle2 className="w-12 h-12 text-[#75E29A] mb-5" />
                                <h2 className="font-display font-black uppercase text-brand-cream text-3xl tracking-tight mb-3">
                                    Request received.
                                </h2>
                                <p className="text-brand-sage leading-relaxed mb-4">
                                    {result.message}
                                </p>
                                <p className="font-mono text-sm text-brand-cream border border-brand-line rounded px-4 py-3 inline-block mb-8">
                                    Reference:{" "}
                                    <span className="text-brand-amber" data-testid="quote-reference-id">
                                        {result.id}
                                    </span>
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <a
                                        href={BUSINESS.phoneTel}
                                        data-testid="quote-success-call-btn"
                                        className="inline-flex items-center gap-2 bg-brand-amber text-brand-ink font-bold px-6 py-3 rounded-full hover:bg-brand-amberHover transition-colors"
                                    >
                                        <Phone className="w-4 h-4" />
                                        Call now instead
                                    </a>
                                    <button
                                        type="button"
                                        data-testid="quote-reset-btn"
                                        onClick={reset}
                                        className="inline-flex items-center gap-2 border border-brand-line text-brand-cream font-semibold px-6 py-3 rounded-full hover:border-brand-amber hover:text-brand-amber transition-colors"
                                    >
                                        Send another request
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form
                                ref={formRef}
                                onSubmit={submit}
                                data-testid="quote-request-form"
                                className="rounded-lg border border-brand-line bg-brand-surface p-6 sm:p-8 space-y-5"
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="q-name" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2">
                                            Name *
                                        </label>
                                        <input
                                            id="q-name"
                                            data-testid="quote-name-input"
                                            className={inputClass}
                                            value={form.name}
                                            onChange={set("name")}
                                            placeholder="Your name"
                                            required
                                            maxLength={120}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="q-phone" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2">
                                            Phone *
                                        </label>
                                        <input
                                            id="q-phone"
                                            data-testid="quote-phone-input"
                                            type="tel"
                                            className={inputClass}
                                            value={form.phone}
                                            onChange={set("phone")}
                                            placeholder="(573) 000-0000"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="q-email" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2">
                                            Email (optional)
                                        </label>
                                        <input
                                            id="q-email"
                                            data-testid="quote-email-input"
                                            type="email"
                                            className={inputClass}
                                            value={form.email}
                                            onChange={set("email")}
                                            placeholder="you@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="q-address" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2">
                                            Address or cross streets
                                        </label>
                                        <input
                                            id="q-address"
                                            data-testid="quote-address-input"
                                            className={inputClass}
                                            value={form.address}
                                            onChange={set("address")}
                                            placeholder="Poplar Bluff or within 30 miles"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="q-size" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2">
                                            Stump size
                                        </label>
                                        <select
                                            id="q-size"
                                            data-testid="quote-size-select"
                                            className={`${inputClass} appearance-none cursor-pointer`}
                                            value={form.stump_size}
                                            onChange={set("stump_size")}
                                        >
                                            <option value="">Not sure — send a photo</option>
                                            {SIZE_PRICING.map((s) => (
                                                <option key={s.size} value={s.size}>
                                                    {s.size}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="q-situation" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2">
                                            Situation
                                        </label>
                                        <select
                                            id="q-situation"
                                            data-testid="quote-situation-select"
                                            className={`${inputClass} appearance-none cursor-pointer`}
                                            value={form.situation}
                                            onChange={set("situation")}
                                        >
                                            <option value="">Select one</option>
                                            {DIFFICULTY.map((d) => (
                                                <option key={d.label} value={d.label}>
                                                    {d.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="q-cleanup" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2">
                                        Cleanup add-ons
                                    </label>
                                    <select
                                        id="q-cleanup"
                                        data-testid="quote-cleanup-select"
                                        className={`${inputClass} appearance-none cursor-pointer`}
                                        value={form.cleanup}
                                        onChange={set("cleanup")}
                                    >
                                        <option value="">Standard cleanup (included)</option>
                                        {CLEANUP_UPSELLS.map((c) => (
                                            <option key={c.label} value={c.label}>
                                                {c.label} — {c.add}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="q-message" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss mb-2">
                                        Anything else? (gates, roots, photo description)
                                    </label>
                                    <textarea
                                        id="q-message"
                                        data-testid="quote-message-input"
                                        rows={4}
                                        className={`${inputClass} resize-none`}
                                        value={form.message}
                                        onChange={set("message")}
                                        placeholder="Narrow gate, big surface roots, multiple stumps..."
                                        maxLength={2000}
                                    />
                                </div>

                                {status === "error" && (
                                    <div
                                        data-testid="quote-error-alert"
                                        className="flex items-start gap-3 border border-red-500/40 bg-red-500/10 rounded px-4 py-3 text-sm text-red-300"
                                    >
                                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                        {errorMsg}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    data-testid="quote-submit-button"
                                    disabled={status === "loading"}
                                    className="w-full inline-flex items-center justify-center gap-3 bg-brand-amber text-brand-ink font-bold text-lg px-8 py-4 rounded-full hover:bg-brand-amberHover transition-colors disabled:opacity-60"
                                >
                                    {status === "loading" ? "Sending..." : "Send my free quote request"}
                                </button>
                            </form>
                        )}
                    </div>

                    <aside className="lg:col-span-2 space-y-5">
                        <Reveal delay={0.1}>
                            <div className="rounded-lg overflow-hidden border border-brand-line clip-notch">
                                <img
                                    src={IMAGES.weatheredStump}
                                    alt="Weathered stump waiting to be ground in a Missouri yard"
                                    className="w-full h-56 object-cover"
                                />
                            </div>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <div className="rounded-lg border border-brand-line bg-brand-surface p-7 space-y-5" data-testid="quote-contact-card">
                                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-amber">
                                    Faster by phone
                                </p>
                                <a
                                    href={BUSINESS.phoneTel}
                                    data-testid="quote-card-phone-link"
                                    className="flex items-center gap-3 text-brand-cream hover:text-brand-amber transition-colors font-bold text-2xl"
                                >
                                    <Phone className="w-6 h-6 text-brand-amber" />
                                    {BUSINESS.phoneDisplay}
                                </a>
                                <a
                                    href={BUSINESS.phoneSms}
                                    data-testid="quote-card-sms-link"
                                    className="inline-flex items-center gap-2 border border-brand-amber/50 text-brand-amber font-semibold text-sm px-4 py-2 rounded-full hover:bg-brand-amber hover:text-brand-ink transition-colors"
                                >
                                    <MessageSquare className="w-4 h-4" />
                                    Text us a stump photo
                                </a>
                                <a
                                    href={`mailto:${BUSINESS.email}`}
                                    data-testid="quote-card-email-link"
                                    className="flex items-center gap-3 text-brand-sage hover:text-brand-amber transition-colors break-all text-sm"
                                >
                                    <Mail className="w-5 h-5 text-brand-amber shrink-0" />
                                    {BUSINESS.email}
                                </a>
                                <p className="flex items-center gap-3 text-brand-sage text-sm">
                                    <Clock className="w-5 h-5 text-brand-amber shrink-0" />
                                    {BUSINESS.hours}
                                </p>
                                <p className="flex items-start gap-3 text-brand-sage text-sm">
                                    <MapPin className="w-5 h-5 text-brand-amber shrink-0 mt-0.5" />
                                    Poplar Bluff, MO — 30-mile radius
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <div className="rounded-lg border border-brand-amber/40 bg-brand-amber/10 p-7" data-testid="quote-guarantee-card">
                                <p className="font-display font-black uppercase text-brand-amber text-3xl tracking-tight mb-2">
                                    Same-week guarantee
                                </p>
                                <p className="text-brand-sage text-sm leading-relaxed">
                                    Call this week, we grind this week. Payment
                                    due when the job's done — cash, card, or
                                    other payment options available.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <p className="text-brand-moss text-xs font-mono uppercase tracking-wider">
                                Prefer to see numbers first?{" "}
                                <Link to="/pricing" data-testid="quote-pricing-link" className="text-brand-amber hover:underline">
                                    Open the price list
                                </Link>
                            </p>
                        </Reveal>
                    </aside>
                </div>
            </section>
        </main>
    );
};

export default Quote;
